import React, { useState, useRef, DragEvent } from "react";
import styled from "styled-components";
import { gql, useMutation } from "@apollo/client";
import { Group, Button as LatitudeButton } from "@flexport/latitude-typescript";
import { semanticColors, fontWeights } from "../styles/latitudeTheme";
import { useNotifications } from "../contexts/NotificationContext";
import { createScanProgressNotification } from "./QuarantineNotifications";
import { QuarantineStatus } from "../generated/graphql";

// Add GraphQL mutations for getting pre-signed URLs and saving file metadata
const GET_UPLOAD_URL = gql`
  mutation GetS3UploadUrl($fileName: String!, $contentType: String!) {
    getS3UploadUrl(fileName: $fileName, contentType: $contentType) {
      uploadUrl
      key
    }
  }
`;

const SAVE_FILE_METADATA = gql`
  mutation SaveFileMetadata(
    $fileName: String!
    $fileKey: String!
    $clientId: ID!
    $extension: String!
  ) {
    saveFileMetadata(
      fileName: $fileName
      fileKey: $fileKey
      clientId: $clientId
      extension: $extension
    ) {
      id
      fileName
      extension
      fileUrl
      createdAt
      clientId
      folderPath
      subfolderPath
      quarantineStatus
      quarantineReason
      quarantineTimestamp
      scanEngine
      scanEngineVersion
      scanStartedAt
      scanCompletedAt
      client {
        clientId
        client_name
        folder
        upload_complete
      }
    }
  }
`;

const UploadContainer = styled.div`
  margin: 20px 0;
  padding: 24px;
  border: 2px dashed ${semanticColors.borderMedium};
  border-radius: 8px;
  text-align: center;
  background-color: ${semanticColors.backgroundSecondary};
  position: relative;
  transition: all 0.2s;

  &:hover {
    border-color: ${semanticColors.primary};
    background-color: ${semanticColors.backgroundHover};
  }
`;

const FileInput = styled.input`
  display: none;
`;

const UploadInstructions = styled.div`
  margin: 10px 0 20px 0;
  color: ${semanticColors.textSecondary};
  font-size: 14px;
  text-align: center;
`;

const UploadIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M11 15V3H13V15H16L12 20L8 15H11Z" fill="currentColor" />
    <path d="M4 22V18H6V20H18V18H20V22H4Z" fill="currentColor" />
  </svg>
);

const FilesList = styled.div`
  margin-top: 16px;
  max-height: 200px;
  overflow-y: auto;
  text-align: left;
`;

const FileItem = styled.div`
  margin-bottom: 8px;
  padding: 10px 12px;
  border-radius: 4px;
  background-color: ${semanticColors.backgroundPrimary};
  border: 1px solid ${semanticColors.borderLight};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FileName = styled.div`
  font-size: 14px;
  color: ${semanticColors.textPrimary};
  word-break: break-all;
  flex: 1;
`;

const FileStatus = styled.div<{
  $status: "pending" | "uploading" | "completed" | "error";
}>`
  font-size: 12px;
  margin-left: 10px;
  font-weight: ${fontWeights.medium};
  color: ${(props) => {
    switch (props.$status) {
      case "pending":
        return semanticColors.textSecondary;
      case "uploading":
        return semanticColors.primary;
      case "completed":
        return semanticColors.success;
      case "error":
        return semanticColors.error;
      default:
        return semanticColors.textSecondary;
    }
  }};
`;

const ProgressBar = styled.div<{ $progress: number }>`
  height: 4px;
  width: 100%;
  background-color: ${semanticColors.borderLight};
  border-radius: 2px;
  margin-top: 6px;
  position: relative;
  overflow: hidden;

  &:after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${(props) => props.$progress}%;
    background-color: ${semanticColors.success};
    transition: width 0.3s ease;
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${semanticColors.error};
  cursor: pointer;
  font-size: 18px;
  padding: 0 8px;
  margin-left: 8px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }

  &:disabled {
    color: ${semanticColors.borderMedium};
    cursor: not-allowed;
  }
`;

const DragOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${semanticColors.primaryLight};
  border: 3px dashed ${semanticColors.primary};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${semanticColors.primary};
  font-size: 20px;
  font-weight: ${fontWeights.bold};
`;

const FilesCounter = styled.div`
  margin-top: 8px;
  font-size: 14px;
  color: ${semanticColors.textSecondary};
`;

const ScanStatusIndicator = styled.div<{ status: QuarantineStatus }>`
  font-size: 12px;
  font-weight: ${fontWeights.medium};
  color: ${(props) => {
    switch (props.status) {
      case QuarantineStatus.Clean:
        return semanticColors.success;
      case QuarantineStatus.Quarantined:
        return semanticColors.error;
      case QuarantineStatus.ScanFailed:
        return semanticColors.warning;
      default:
        return semanticColors.textSecondary;
    }
  }};
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;

  &::before {
    content: ${(props) => {
      switch (props.status) {
        case QuarantineStatus.Clean:
          return '"✅"';
        case QuarantineStatus.Quarantined:
          return '"⚠️"';
        case QuarantineStatus.ScanFailed:
          return '"❌"';
        default:
          return '"⏳"';
      }
    }};
  }
`;

interface UploadResponse {
  id: string;
  fileName: string;
  extension: string;
  fileUrl: string;
  createdAt: string;
  clientId: string;
  quarantineStatus: QuarantineStatus;
  quarantineReason?: string | null;
  quarantineTimestamp?: string | null;
  scanEngine?: string | null;
  scanEngineVersion?: string | null;
  scanStartedAt?: string | null;
  scanCompletedAt?: string | null;
  client: {
    clientId: string;
    client_name: string;
    folder: string;
    upload_complete: boolean;
  };
}

interface FileUploadProps {
  onUploadComplete?: (fileData: UploadResponse) => void;
  onUploadError?: (error: Error) => void;
  allowedFileTypes?: string[];
  clientId: string;
  maxParallelUploads?: number;
  userData?: any;
}

interface FileUploadState {
  id: string;
  file: File;
  progress: number;
  status: "pending" | "uploading" | "completed" | "error";
  error?: string;
  quarantineStatus?: QuarantineStatus;
  scanEngine?: string | null;
  uploadResponse?: UploadResponse;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onUploadComplete,
  onUploadError,
  allowedFileTypes = [],
  clientId,
  maxParallelUploads = 10,
  userData,
}) => {
  const [files, setFiles] = useState<FileUploadState[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Add Apollo hooks for mutations
  const [getUploadUrl] = useMutation(GET_UPLOAD_URL);
  const [saveFileMetadata] = useMutation(SAVE_FILE_METADATA);

  // Add notifications hook
  const { addNotification } = useNotifications();

  const generateFileId = () =>
    `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const getScanStatusText = (fileState: FileUploadState) => {
    if (fileState.status !== "completed" || !fileState.uploadResponse) {
      return null;
    }

    const response = fileState.uploadResponse;

    if (response.quarantineStatus === QuarantineStatus.Clean) {
      // Hide the clean message to avoid confusing UX while background scanning may update later
      return null;
    } else if (response.quarantineStatus === QuarantineStatus.Quarantined) {
      return (
        "Quarantined - " +
        (response.quarantineReason || "Security threat detected")
      );
    } else if (response.quarantineStatus === QuarantineStatus.ScanFailed) {
      return "Scan failed - Manual review needed";
    } else {
      return "Scan pending...";
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      processSelectedFiles(selectedFiles);
    }
  };

  const processSelectedFiles = (selectedFiles: File[]) => {
    // No need to limit total number of files, only check file types

    // Check file types if allowed types are specified
    const validFiles = selectedFiles.filter((file) => {
      if (allowedFileTypes.length === 0) return true;

      const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";
      return (
        allowedFileTypes.includes(`.${fileExtension}`) ||
        allowedFileTypes.includes(fileExtension)
      );
    });

    if (validFiles.length < selectedFiles.length) {
      alert(
        `Some files were rejected because their type is not allowed. Allowed types: ${allowedFileTypes.join(", ")}`,
      );
    }

    // Add valid files to state
    setFiles((prev) => [
      ...prev,
      ...validFiles.map((file) => ({
        id: generateFileId(),
        file,
        progress: 0,
        status: "pending" as const,
      })),
    ]);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleBrowseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      processSelectedFiles(droppedFiles);
    }
  };

  const handleRemoveFile = (fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  // Check if user has upload permission
  const hasUploadPermission = React.useMemo(() => {
    if (!userData) return false;

    // Admin users can always upload
    if (userData.role === "ADMIN") return true;

    // Check permissions based on role
    if (userData.clientId === clientId) {
      return userData.role === "CUSTOMER" || userData.role === "SUPPLIER";
    }

    return false;
  }, [userData, clientId]);

  const uploadFile = async (fileState: FileUploadState) => {
    const { id, file } = fileState;

    if (!clientId) {
      setFiles((prev) =>
        prev.map((f) =>
          f.id === id
            ? { ...f, status: "error", error: "Client ID is required" }
            : f,
        ),
      );
      if (onUploadError) onUploadError(new Error("Client ID is required"));
      return;
    }

    // Update status to uploading
    setFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, status: "uploading" } : f)),
    );

    try {
      // Step 1: Get pre-signed URL from backend
      const { data } = await getUploadUrl({
        variables: {
          fileName: file.name,
          contentType: file.type || "application/octet-stream",
        },
      });

      const { uploadUrl, key } = data.getS3UploadUrl;

      // Step 2: Upload directly to S3 using the pre-signed URL
      const xhr = new XMLHttpRequest();

      // Set up progress tracking
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const progressPercent = Math.round(
            (event.loaded / event.total) * 100,
          );
          setFiles((prev) =>
            prev.map((f) =>
              f.id === id ? { ...f, progress: progressPercent } : f,
            ),
          );
        }
      });

      // Handle upload events
      xhr.addEventListener("load", async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // Step 3: Save file metadata (wrap in try/catch to surface backend validation errors)
          try {
            const fileExtension = file.name.split(".").pop() || "";
            const res = await saveFileMetadata({
              variables: {
                fileName: file.name,
                fileKey: key,
                clientId: clientId,
                extension: fileExtension,
              },
            });

            const uploadResponse = res?.data?.saveFileMetadata;
            if (!uploadResponse) {
              throw new Error("Upload could not be processed.");
            }

            // Update file status with quarantine information
            setFiles((prev) =>
              prev.map((f) =>
                f.id === id
                  ? {
                      ...f,
                      status: "completed",
                      quarantineStatus: uploadResponse.quarantineStatus,
                      scanEngine: uploadResponse.scanEngine,
                      uploadResponse,
                    }
                  : f,
              ),
            );

            // Show scan progress notification
            addNotification(createScanProgressNotification(file.name, false));

            // Notify component caller
            if (onUploadComplete) onUploadComplete(uploadResponse);
          } catch (e: any) {
            const graphQLErrors = (e && e.graphQLErrors) || [];
            const errMsg = graphQLErrors.length
              ? graphQLErrors.map((g: any) => g.message).join("; ")
              : e?.message || "Upload could not be processed.";

            console.error("[Upload] saveFileMetadata failed:", errMsg, e);

            // Mark this file as error so UI reflects failure instead of hanging at 100%
            setFiles((prev) =>
              prev.map((f) =>
                f.id === id
                  ? {
                      ...f,
                      status: "error",
                      error: errMsg,
                    }
                  : f,
              ),
            );

            // Surface a visible notification to the user
            addNotification({
              id: `upload-error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              type: "error",
              title: "Upload rejected",
              message: errMsg,
              fileName: file.name,
              autoClose: false,
            });

            if (onUploadError) onUploadError(new Error(errMsg));
          }
        } else {
          console.error(
            `[Upload] HTTP error during upload for ${file.name}: ${xhr.status} ${xhr.statusText}`,
          );
          setFiles((prev) =>
            prev.map((f) =>
              f.id === id
                ? {
                    ...f,
                    status: "error",
                    error: `HTTP error: ${xhr.status} ${xhr.statusText}`,
                  }
                : f,
            ),
          );
          if (onUploadError)
            onUploadError(
              new Error(
                `HTTP error during upload: ${xhr.status} ${xhr.statusText}`,
              ),
            );
        }
      });

      xhr.addEventListener("error", () => {
        console.error(`[Upload] Network error during upload for ${file.name}`);
        setFiles((prev) =>
          prev.map((f) =>
            f.id === id
              ? {
                  ...f,
                  status: "error",
                  error: "Network error during upload",
                }
              : f,
          ),
        );
        if (onUploadError)
          onUploadError(new Error("Network error during upload"));
      });

      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader(
        "Content-Type",
        file.type || "application/octet-stream",
      );
      xhr.send(file);
    } catch (error: any) {
      console.error(
        `[Upload] Error during upload process for ${file.name}:`,
        error,
      );

      // Update file status to error
      setFiles((prev) =>
        prev.map((f) =>
          f.id === id
            ? {
                ...f,
                status: "error",
                error: error instanceof Error ? error.message : String(error),
              }
            : f,
        ),
      );

      if (onUploadError) {
        onUploadError(
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    }
  };

  const handleUploadAll = async () => {
    const pendingFiles = files.filter((f) => f.status === "pending");

    if (pendingFiles.length === 0) return;

    // Get count of currently uploading files
    const currentlyUploading = files.filter(
      (f) => f.status === "uploading",
    ).length;

    // Calculate how many more files we can upload in parallel
    const availableSlots = maxParallelUploads - currentlyUploading;

    if (availableSlots <= 0) {
      alert(
        `Already uploading ${currentlyUploading} files. Please wait for some uploads to complete before starting more.`,
      );
      return;
    }

    // Get the files to upload based on available slots
    const filesToUpload = pendingFiles.slice(0, availableSlots);

    // Upload files in parallel
    await Promise.allSettled(
      filesToUpload.map((fileState) => uploadFile(fileState)),
    );
  };

  // Calculate counts for different file statuses
  const pendingFilesCount = files.filter((f) => f.status === "pending").length;
  const currentlyUploadingCount = files.filter(
    (f) => f.status === "uploading",
  ).length;
  const completedFilesCount = files.filter(
    (f) => f.status === "completed",
  ).length;
  const errorFilesCount = files.filter((f) => f.status === "error").length;
  const hasUploadingFiles = currentlyUploadingCount > 0;

  // Manage file list with potential to have many files
  const [showCompleted, setShowCompleted] = useState(true);

  // Filter files for display based on status
  const filesToDisplay = files.filter(
    (f) =>
      f.status === "pending" ||
      f.status === "uploading" ||
      f.status === "error" ||
      (f.status === "completed" && showCompleted),
  );

  return (
    <UploadContainer
      ref={containerRef}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-testid="file-upload-container"
    >
      {!hasUploadPermission && (
        <Message>
          You don't have permission to upload files for this client.
        </Message>
      )}

      {hasUploadPermission && (
        <>
          {dragOver && <DragOverlay>Drop files here</DragOverlay>}

          <Group flexDirection="column" alignItems="center" gap={8}>
            <UploadInstructions>
              Drop files here or click to browse
            </UploadInstructions>

            <LatitudeButton
              label="Browse for files"
              intent="basic"
              kind={files.length > 0 ? "hollow" : "solid"}
              size="m"
              isLoading={false}
              width="responsive"
              type="button"
              disabled={false}
              onClick={handleBrowseClick}
              data-testid="file-browse-button"
            >
              <UploadIcon /> Browse for files
            </LatitudeButton>
          </Group>

          <FileInput
            type="file"
            onChange={handleFileChange}
            ref={fileInputRef}
            accept={allowedFileTypes.join(",")}
            multiple
            data-testid="file-upload-input"
          />

          {files.length > 0 && (
            <FilesCounter>
              {pendingFilesCount > 0 ? (
                <strong>
                  {pendingFilesCount} file{pendingFilesCount !== 1 ? "s" : ""}{" "}
                  ready to upload
                </strong>
              ) : hasUploadingFiles ? (
                <span>Processing uploads...</span>
              ) : (
                <span>
                  {completedFilesCount > 0 &&
                    `${completedFilesCount} completed`}
                  {completedFilesCount > 0 && errorFilesCount > 0 && " • "}
                  {errorFilesCount > 0 && `${errorFilesCount} failed`}
                </span>
              )}

              {hasUploadingFiles &&
                ` • ${currentlyUploadingCount}/${maxParallelUploads} uploading`}

              {files.some((f) => f.status === "completed") && (
                <ToggleCompletedButton
                  onClick={() => setShowCompleted(!showCompleted)}
                  type="button"
                >
                  {showCompleted ? "Hide completed" : "Show completed"}
                </ToggleCompletedButton>
              )}
            </FilesCounter>
          )}

          {filesToDisplay.length > 0 && (
            <FilesList>
              {filesToDisplay.map((fileState) => (
                <FileItem key={fileState.id}>
                  <div style={{ flex: 1 }}>
                    <FileName>
                      {fileState.file.name} (
                      {(fileState.file.size / 1024).toFixed(2)} KB)
                    </FileName>
                    <ProgressBar $progress={fileState.progress} />
                    <FileStatus $status={fileState.status}>
                      {fileState.status === "pending" && "Ready to upload"}
                      {fileState.status === "uploading" &&
                        `Uploading ${fileState.progress}%`}
                      {fileState.status === "completed" && "Upload completed"}
                      {fileState.status === "error" &&
                        (fileState.error || "Failed")}
                    </FileStatus>
                    {fileState.status === "completed" &&
                      fileState.uploadResponse &&
                      getScanStatusText(fileState) && (
                        <ScanStatusIndicator
                          status={fileState.uploadResponse.quarantineStatus}
                        >
                          {getScanStatusText(fileState)}
                        </ScanStatusIndicator>
                      )}
                  </div>
                  <RemoveButton
                    onClick={() => handleRemoveFile(fileState.id)}
                    disabled={fileState.status === "uploading"}
                    title={
                      fileState.status === "uploading"
                        ? "Cannot remove while uploading"
                        : "Remove file"
                    }
                  >
                    ×
                  </RemoveButton>
                </FileItem>
              ))}
            </FilesList>
          )}

          {pendingFilesCount > 0 && (
            <Group flexDirection="column" alignItems="center" gap={8}>
              <LatitudeButton
                label="Upload Files"
                intent="basic"
                kind="solid"
                size="m"
                isLoading={hasUploadingFiles}
                width="responsive"
                type="button"
                disabled={
                  currentlyUploadingCount >= maxParallelUploads ||
                  !hasUploadPermission
                }
                onClick={handleUploadAll}
                data-testid="file-upload-all-button"
              >
                {hasUploadingFiles ? (
                  <>
                    <LoadingIcon /> Uploading...
                  </>
                ) : (
                  <>
                    <UploadStartIcon /> Upload Files
                  </>
                )}
              </LatitudeButton>
            </Group>
          )}
        </>
      )}
    </UploadContainer>
  );
};

const LoadingIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="loading-icon"
  >
    <path
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeDasharray="60 15"
    />
    <style>{`
      @keyframes rotate {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      .loading-icon {
        animation: rotate 1.5s linear infinite;
      }
    `}</style>
  </svg>
);

const UploadStartIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 19H20V12H22V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V12H4V19Z"
      fill="currentColor"
    />
    <path d="M13 9V3H11V9H8L12 14L16 9H13Z" fill="currentColor" />
  </svg>
);

const ToggleCompletedButton = styled.button`
  background: none;
  border: none;
  color: ${semanticColors.primary};
  font-size: 14px;
  font-weight: ${fontWeights.medium};
  cursor: pointer;
  margin-left: 12px;
  text-decoration: underline;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

// Add Message component for error/info messages
const Message = styled.div`
  padding: 20px;
  background-color: ${semanticColors.errorLight};
  border-radius: 4px;
  margin-bottom: 20px;
  border: 1px solid ${semanticColors.error};
  color: ${semanticColors.error};
  font-weight: ${fontWeights.medium};
`;

export default FileUpload;
